<script setup lang="ts">
import { toast } from 'vue-sonner'
import { useMediaQuery } from '@vueuse/core'
import type { FeedActionSheetMode, PublishedSummary } from '@/composables/feed/use-feed-actions';
import { useClient } from '@/stores/client';
import { useApiResponses } from '@/composables/api/responses';
import { useApiVacancies } from '@/composables/api/vacancies';
import { onlineLocationList } from '@/composables/online-location-list';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AppMaskedDateInput from '@/components/form/AppMaskedDateInput.vue';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import ResponsiveOverlay from '@/components/overlay/ResponsiveOverlay.vue';
import FeedActionPrompt from '@/components/feed/FeedActionPrompt.vue';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const props = defineProps<{
  mode: FeedActionSheetMode | null;
  summaries: PublishedSummary[];
  nested?: boolean;
}>();

const emit = defineEmits<{
  'update:mode': [mode: FeedActionSheetMode | null],
  responded: [payload: { itemId: string; responseId: string }],
  invited: [],
  complained: [],
}>();
const client = useClient();
const isMobile = useMediaQuery('(max-width: 767px)');
const apiResponses = useApiResponses();
const apiVacancies = useApiVacancies();

const open = computed({
  get: () => props.mode !== null,
  set: (value: boolean) => {
    if (!value) {
      emit('update:mode', null);
    }
  },
});

function close(): void {
  emit('update:mode', null);
}

function getShareUrl(): string {
  if (props.mode === null) {
    return '';
  }

  const path = props.mode.item.type === 'vacancy'
    ? `/vacancies/${props.mode.item.id}`
    : `/summaries/${props.mode.item.id}`;

  return `${window.location.origin}${path}`;
}

function copyLink(): void {
  const url = getShareUrl();
  if (url === '') {
    return;
  }

  void navigator.clipboard.writeText(url).then(
    () => {
      toast.success('Ссылка скопирована');
      close();
    },
    () => {
      toast.error('Не удалось скопировать ссылку');
    },
  );
}

async function shareOrCopy(): Promise<void> {
  const url = getShareUrl();
  if (!url) return;

  if (!isMobile.value || typeof navigator.share !== 'function') {
    copyLink();
    return;
  }

  try {
    await navigator.share({ url });
    close();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    copyLink();
  }
}

type CompanyVacancy = {
  id: string;
  title: string;
};

type InviteType = 'question' | 'interview' | 'offer';

const INVITE_TYPE_META: Record<InviteType, { title: string; type: number; heading: string }> = {
  question: { title: 'Собеседование', type: 5, heading: 'Пригласить на собеседование' },
  interview: { title: 'Доп. вопросы', type: 4, heading: 'Задать дополнительные вопросы' },
  offer: { title: 'Работа', type: 7, heading: 'Пригласить на работу' },
};

const companyVacancies = ref<CompanyVacancy[]>([]);
const inviteCompanyId = ref<string | null>(null);
const inviteVacancyId = ref<string | null>(null);
const inviteType = ref<InviteType>('question');
const invitePlatform = ref<0 | 1>(1);
const invitePlace = ref<string>(onlineLocationList[0]);
const inviteDate = ref('');
const inviteComment = ref('');
const inviteLoading = ref(false);
const inviteForceTime = ref(false);
// The company's vacancies have been fetched: an empty list then means there is nobody to invite to.
const vacanciesLoaded = ref(false);
const needVacancy = computed(() => vacanciesLoaded.value && companyVacancies.value.length === 0 && inviteCompanyId.value !== null);

function setInviteType(value: string | undefined): void {
  if (value === 'question' || value === 'interview' || value === 'offer') inviteType.value = value;
}

function setInvitePlatform(value: string | undefined): void {
  if (value !== 'online' && value !== 'office') return;
  invitePlatform.value = value === 'online' ? 1 : 0;
  invitePlace.value = value === 'online' ? onlineLocationList[0] : '';
}

function resetInviteForm(): void {
  inviteVacancyId.value = null;
  inviteDate.value = '';
  invitePlatform.value = 1;
  invitePlace.value = onlineLocationList[0];
  inviteComment.value = '';
  inviteForceTime.value = false;
}

async function ensureInviteState(): Promise<void> {
  if (props.mode === null || props.mode.name !== 'invite') {
    return;
  }

  const mode = props.mode;

  const companyId = client.getActiveCompany?.id ?? null;
  if (companyId === null) {
    return;
  }

  inviteType.value = mode.inviteType;
  resetInviteForm();

  if (inviteCompanyId.value !== companyId || companyVacancies.value.length === 0) {
    inviteCompanyId.value = companyId;
    companyVacancies.value = [];
    vacanciesLoaded.value = false;

    const response = await apiVacancies.get(companyId, 'company');
    if (props.mode !== mode) {
      return;
    }

    const data = response.data.value;
    if (response.error.value || !Array.isArray(data?.vacancies?.items)) {
      return;
    }

    vacanciesLoaded.value = true;
    companyVacancies.value = (data.vacancies.items as { id: string; city: { title: string }; profession: { title: string } }[])
      .map((item) => ({
        id: item.id,
        title: `${item.city.title}, ${item.profession.title}`,
      }));
  }

  const matched = mode.item.type === 'summary'
    ? companyVacancies.value.find((item) => item.id === mode.item.matchedVacancyId)
    : undefined;
  inviteVacancyId.value = matched?.id ?? (companyVacancies.value.length === 1 ? companyVacancies.value[0].id : null);
}

watch(() => props.mode, () => {
  void ensureInviteState();
}, { immediate: true });

watch([inviteVacancyId, inviteType, inviteDate, invitePlace], () => {
  inviteForceTime.value = false;
});

const canSubmitInvite = computed(() => {
  if (inviteVacancyId.value === null) {
    return false;
  }

  if (inviteType.value === 'question') {
    return inviteDate.value !== '' && invitePlace.value.trim() !== '';
  }

  return true;
});

async function submitInvite(): Promise<void> {
  if (
    props.mode === null
    || props.mode.name !== 'invite'
    || props.mode.item.type !== 'summary'
    || inviteVacancyId.value === null
    || inviteLoading.value
  ) {
    return;
  }

  inviteLoading.value = true;
  try {
    const startTime = inviteDate.value === ''
      ? Math.floor(Date.now() / 1000) + 86400
      : Math.floor(new Date(inviteDate.value).getTime() / 1000);

    const response = await apiResponses.invite({
      summary: props.mode.item.id,
      vacancy: inviteVacancyId.value,
      startTime,
      platform: invitePlatform.value,
      location: invitePlace.value,
      description: inviteComment.value,
      message: inviteComment.value,
      type: INVITE_TYPE_META[inviteType.value].type,
    }, inviteForceTime.value);

    if (!response.error.value) {
      toast.success('Успешно');
      emit('invited');
      close();
      return;
    }

    if (response.error.value.statusCode === 409 && apiErrorBody(response.error.value)?.details?.isForceTime === true) {
      inviteForceTime.value = true;
    }
    toast.error(apiErrorMessage(response.error.value, 'Ошибка'));
  } finally {
    inviteLoading.value = false;
  }
}

const complaintMessage = ref('');
const complaintLoading = ref(false);

watch(() => props.mode, (mode) => {
  if (mode?.name === 'complaint') {
    complaintMessage.value = '';
  }
});

async function submitComplaint(): Promise<void> {
  if (props.mode === null || props.mode.name !== 'complaint' || complaintLoading.value) {
    return;
  }

  const message = complaintMessage.value.trim();
  if (message === '' || message.length > 120) {
    toast.error(message === '' ? 'Введите текст' : 'Длина текста не должна превышать 120 символов');
    return;
  }

  complaintLoading.value = true;
  try {
    const response = await mainFetch('complaints', {
      method: 'POST',
      body: {
        source: props.mode.item.type === 'vacancy' ? 1 : 0,
        status: 0,
        entity: props.mode.item.id,
        message,
      },
    });
    if (!response.error.value) {
      toast.success('Жалоба отправлена');
      emit('complained');
      close();
    } else {
      toast.error(apiErrorMessage(response.error.value, 'Ошибка'));
    }
  } finally {
    complaintLoading.value = false;
  }
}

function openComplaint(): void {
  if (props.mode === null || props.mode.name !== 'more') {
    return;
  }

  emit('update:mode', { name: 'complaint', item: props.mode.item });
}

const sheetTitle = computed(() => {
  if (props.mode === null) {
    return '';
  }

  switch (props.mode.name) {
    case 'respond-pick-summary':
      return 'С каким резюме откликнуться?';
    case 'respond-need-summary':
      return props.mode.draftId ? 'Продолжите резюме' : 'Нужно видеорезюме';
    case 'invite':
      return needVacancy.value ? 'Нужна вакансия' : INVITE_TYPE_META[inviteType.value].heading;
    case 'more':
      return 'Действия';
    case 'complaint':
      return `Пожаловаться на ${props.mode.item.type === 'vacancy' ? 'вакансию' : 'резюме'}`;
  }
});
</script>

<template>
  <ResponsiveOverlay
    :open="open"
    :nested="nested"
    :title="sheetTitle"
    :description="mode?.name === 'invite' && inviteForceTime ? 'В указанное время (±10 мин.) уже есть собеседования. Нажмите «Пригласить» ещё раз, чтобы подтвердить.' : undefined"
    desktop-class="max-w-xl"
    mobile-class="max-h-[calc(100dvh-6rem)]"
    @update:open="open = $event"
  >
      <div class="min-h-0 overflow-y-auto px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-4">
        <template v-if="mode?.name === 'respond-pick-summary'">
          <div class="flex flex-col gap-2">
            <Button
              v-for="summary in summaries"
              :key="summary.id"
              type="button"
              variant="outline"
              class="h-12 justify-start rounded-xl text-base font-normal"
              @click="emit('responded', { itemId: mode.item.id, responseId: summary.id }); close()"
            >
              {{ summary.title }}
            </Button>
          </div>
        </template>

        <template v-else-if="mode?.name === 'respond-need-summary'">
          <FeedActionPrompt
            :text="mode.draftId
              ? 'Откликаться можно опубликованным резюме, а у вас пока черновик. Осталось заполнить несколько шагов и опубликовать его.'
              : 'Откликаться можно видеорезюме. Запишите короткие ответы на вопросы — это займёт пару минут.'"
            :action-label="mode.draftId ? 'Продолжить резюме' : 'Создать резюме'"
            :to="mode.draftId ? `/applicant/summaries/edit/${mode.draftId}` : '/applicant/summaries/create'"
            @close="close()"
          />
        </template>

        <template v-else-if="mode?.name === 'invite' && needVacancy">
          <FeedActionPrompt
            text="Чтобы пригласить кандидата, сначала создайте вакансию: на неё придут отклики и собеседования."
            action-label="Создать вакансию"
            :to="`/company/${inviteCompanyId}/vacancies/create`"
            @close="close()"
          />
        </template>

        <template v-else-if="mode?.name === 'invite'">
          <div class="flex flex-col gap-4">
            <Field>
              <FieldLabel for="feed-invite-vacancy" required>Вакансия</FieldLabel>
              <Select v-model="inviteVacancyId">
                <SelectTrigger id="feed-invite-vacancy" size="lg" class="w-full rounded-xl">
                  <SelectValue placeholder="Выберите вакансию" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem v-for="vacancy in companyVacancies" :key="vacancy.id" :value="vacancy.id">
                      {{ vacancy.title }}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>

            <ToggleGroup type="single" :model-value="inviteType" variant="outline" :spacing="2" aria-label="Тип приглашения" class="flex w-full gap-2" @update:model-value="setInviteType">
              <ToggleGroupItem
                v-for="(meta, type) in INVITE_TYPE_META"
                :key="type"
                :value="type"
                class="h-10 flex-1 rounded-full text-sm font-normal data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                {{ meta.title }}
              </ToggleGroupItem>
            </ToggleGroup>

            <template v-if="inviteType === 'question'">
              <ToggleGroup type="single" :model-value="invitePlatform === 1 ? 'online' : 'office'" variant="outline" :spacing="2" aria-label="Формат собеседования" class="flex w-full gap-2" @update:model-value="setInvitePlatform">
                <ToggleGroupItem value="online" class="h-10 flex-1 rounded-full text-sm font-normal data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                  Онлайн
                </ToggleGroupItem>
                <ToggleGroupItem value="office" class="h-10 flex-1 rounded-full text-sm font-normal data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">
                  В офис
                </ToggleGroupItem>
              </ToggleGroup>

              <Field>
                <FieldLabel for="feed-invite-date" required>Дата и время</FieldLabel>
                <AppMaskedDateInput
                  id="feed-invite-date"
                  v-model="inviteDate"
                  time
                  class="h-12 rounded-xl"
                />
              </Field>

              <Field>
                <FieldLabel for="feed-invite-place" required>{{ invitePlatform === 0 ? 'Адрес' : 'Площадка' }}</FieldLabel>
                <Select
                  v-if="invitePlatform === 1"
                  v-model="invitePlace"
                >
                  <SelectTrigger id="feed-invite-place" size="lg" class="w-full rounded-xl">
                    <SelectValue placeholder="Выберите площадку" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem v-for="place in onlineLocationList" :key="place" :value="place">
                        {{ place }}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <Input
                  v-else
                  id="feed-invite-place"
                  v-model="invitePlace"
                  type="text"
                  class="h-12 rounded-xl"
                  placeholder="Укажите адрес"
                />
              </Field>
            </template>

            <Field>
              <FieldLabel for="feed-invite-comment">Комментарий</FieldLabel>
              <Textarea
                id="feed-invite-comment"
                v-model="inviteComment"
                class="min-h-20 rounded-xl"
                :maxlength="inviteType === 'offer' ? 200 : undefined"
                placeholder="Детали приглашения"
              />
              <FieldDescription
                v-if="inviteType === 'offer'"
                class="m-0 text-xs text-muted-foreground"
              >
                {{ inviteComment.length }}/200
              </FieldDescription>
            </Field>

            <Button
              type="button"
              class="h-12 rounded-full text-base font-normal"
              :disabled="!canSubmitInvite || inviteLoading"
              @click="submitInvite"
            >
              Пригласить
            </Button>
          </div>
        </template>

        <template v-else-if="mode?.name === 'more'">
          <div class="flex flex-col gap-1">
            <Button
              type="button"
              variant="ghost"
              class="h-12 justify-start rounded-xl text-base font-normal"
              @click="shareOrCopy"
            >
              <Icon
                :name="isMobile ? 'tabler:share' : 'tabler:link-filled'"
                class="mr-2 size-5"
                aria-hidden="true"
              />
              {{ isMobile ? 'Поделиться' : 'Копировать ссылку' }}
            </Button>
            <Button
              type="button"
              variant="ghost"
              class="h-12 justify-start rounded-xl text-base font-normal"
              as-child
            >
              <NuxtLink
                :to="mode.item.type === 'vacancy' ? `/vacancies/${mode.item.id}` : `/summaries/${mode.item.id}`"
                target="_blank"
              >
                <Icon
                  name="tabler:external-link-filled"
                  class="mr-2 size-5"
                  aria-hidden="true"
                />
                Открыть в новой вкладке
              </NuxtLink>
            </Button>
            <Button
              type="button"
              variant="ghost"
              class="h-12 justify-start rounded-xl text-base font-normal text-destructive hover:text-destructive"
              @click="openComplaint"
            >
              <Icon
                name="tabler:flag-filled"
                class="mr-2 size-5"
                aria-hidden="true"
              />
              Пожаловаться
            </Button>
          </div>
        </template>

        <template v-else-if="mode?.name === 'complaint'">
          <div class="flex flex-col gap-3">
            <Field>
              <FieldLabel for="feed-complaint-message" required>Текст жалобы</FieldLabel>
              <Textarea
                id="feed-complaint-message"
                v-model="complaintMessage"
                class="min-h-28 rounded-xl"
                maxlength="120"
                rows="4"
                placeholder="Опишите проблему"
              />
              <FieldDescription>
                {{ complaintMessage.length }}/120
              </FieldDescription>
            </Field>
            <div class="flex gap-2">
              <Button
                type="button"
                variant="outline"
                class="h-12 flex-1 rounded-full text-base font-normal"
                @click="close"
              >
                Отмена
              </Button>
              <Button
                type="button"
                class="h-12 flex-1 rounded-full text-base font-normal"
                :disabled="complaintLoading"
                @click="submitComplaint"
              >
                Отправить
              </Button>
            </div>
          </div>
        </template>
      </div>
  </ResponsiveOverlay>
</template>
